import axios from 'axios'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'

let apiUrl = import.meta.env.VITE_API_URL

// Force HTTPS si on détecte HTTP dans l'URL d'environnement (hors dev local)
if (!import.meta.env.DEV && apiUrl && apiUrl.startsWith('http://')) {
  apiUrl = apiUrl.replace('http://', 'https://')
}

if (!apiUrl) {
  if (import.meta.env.DEV) {
    apiUrl = '/api'
  } else {
    apiUrl = 'https://logelite.fr/api/v1'
  }
} else {
  // Nettoyer l'URL en supprimant les espaces et caractères indésirables
  apiUrl = apiUrl.trim()

  // Forcer HTTPS en production
  if (!import.meta.env.DEV && apiUrl.startsWith('http://')) {
    apiUrl = apiUrl.replace('http://', 'https://')
  }
}

// Double vérification : forcer HTTPS si on est sur un site HTTPS
if (
  typeof window !== 'undefined' &&
  window.location.protocol === 'https:' &&
  apiUrl.startsWith('http://')
) {
  apiUrl = apiUrl.replace('http://', 'https://')
}

// Dernière vérification de sécurité : s'assurer qu'on n'a pas de fragments HTTP
if (!import.meta.env.DEV && apiUrl.includes('http://')) {
  apiUrl = apiUrl.replace(/http:\/\//g, 'https://')
}

// Vérification finale ultra-agressive pour éviter les Mixed Content
if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
  if (apiUrl.includes('http://')) {
    apiUrl = apiUrl.replace(/http:\/\//g, 'https://')
  }
  // Double vérification avec une expression régulière plus stricte
  apiUrl = apiUrl.replace(/^http:\/\//i, 'https://')
}

// VÉRIFICATION FINALE : Force HTTPS en production, quel que soit ce qui s'est passé avant
if (!import.meta.env.DEV) {
  apiUrl = apiUrl.replace(/^http:\/\//i, 'https://')
}

const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  timeout: 30000
})

// Vérification de sécurité post-création pour forcer HTTPS en production
if (!import.meta.env.DEV && api.defaults.baseURL && api.defaults.baseURL.startsWith('http://')) {
  api.defaults.baseURL = api.defaults.baseURL.replace('http://', 'https://')
}

api.interceptors.request.use(
  (config) => {
    // Fonction helper pour forcer HTTPS
    const forceHttps = (url) => {
      if (!url) return url
      return url.replace(/^http:\/\//i, 'https://')
    }

    // Validation de sécurité: s'assurer que l'URL de base est en HTTPS en production
    if (!import.meta.env.DEV) {
      if (config.baseURL) {
        const originalBaseURL = config.baseURL
        config.baseURL = forceHttps(config.baseURL)
      }

      if (config.url) {
        const originalUrl = config.url
        config.url = forceHttps(config.url)
      }
    }

    // Vérification supplémentaire pour l'URL complète
    if (
      !import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:'
    ) {
      const fullUrl = config.baseURL + (config.url || '')
      if (fullUrl.includes('http://')) {
        config.baseURL = forceHttps(config.baseURL)
        config.url = forceHttps(config.url)
      }
    }

    const token =
      localStorage.getItem('access_token') ||
      document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/)?.[1]

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Portée par défaut : le centre sur lequel l'utilisateur est connecté,
    // superadmin compris (le back autorise ce dernier sur n'importe quel
    // centre, mais filtre bien sur celui transmis).
    // - un appel qui fixe lui-même l'en-tête reste prioritaire (ex. choix
    //   d'un autre centre dans un formulaire) ;
    // - skipEstablishmentScope permet la vue multi-centres explicite,
    //   réservée aux rôles autorisés à omettre l'en-tête.
    const auth = useAuthStore()
    if (
      !config.skipEstablishmentScope &&
      auth.currentEstablishmentId &&
      !config.headers['X-Establishment-Id']
    ) {
      config.headers['X-Establishment-Id'] = auth.currentEstablishmentId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Vérifier si nous recevons du HTML au lieu de JSON
    if (error.response?.headers['content-type']?.includes('text/html')) {
      // API returned HTML instead of JSON - likely a proxy or configuration issue

      // Rejeter avec une erreur plus explicite
      const apiError = new Error(
        `API Configuration Error: Received HTML instead of JSON from ${error.config?.url}`
      )
      apiError.originalError = error
      return Promise.reject(apiError)
    }

    // Vérifier si l'erreur est due à une mauvaise URL d'API
    if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      // Cannot connect to API server
    }

    if (error.response?.status === 401) {
      const authStore = useAuthStore()
      await authStore.logout('Session expirée, veuillez vous reconnecter')
    }
    return Promise.reject(error)
  }
)

export default api
